import { useAtom } from "jotai";
import { useEffect } from "react";
import type { ReactNode } from "react";
import {
  BarChart3,
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  MapPin,
  MessageSquare,
  Receipt,
  Route,
  Settings,
  ShoppingCart,
  Truck,
  User,
  Users,
  Warehouse,
  X,
} from "lucide-react";
import { AppLogo } from "@/components/ui/AppLogo";
import { BrandMark } from "@/components/ui/BrandMark";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { getUnreadCount } from "@/api/chat";
import { getNewOrdersCount } from "@/api/orders";
import { useBranding } from "@/contexts/BrandContext";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/auth";
import { sidebarCollapsedAtom, sidebarMobileOpenAtom } from "@/store/sidebar";
import { NavItem } from "./NavItem";

function getUserInitials(
  firstName: string | undefined,
  lastName: string | undefined,
): string {
  const first = firstName?.[0]?.toUpperCase() ?? "";
  const last = lastName?.[0]?.toUpperCase() ?? "";
  return first + last || "?";
}

interface NavGroupProps {
  /** Already-translated, Title-Case group header (uppercased via CSS). */
  title: string;
  collapsed: boolean;
  /** Whether this is the first rendered group (no leading divider when collapsed). */
  first: boolean;
  children: ReactNode;
}

/**
 * Renders a labelled nav group. When expanded it shows a tiny uppercase muted
 * header above its items; when collapsed it shows a thin divider separating the
 * icon clusters instead (no divider before the first group). Callers must only
 * render a NavGroup when it has at least one visible item — an empty group must
 * render nothing.
 */
function NavGroup({ title, collapsed, first, children }: NavGroupProps) {
  return (
    <div className="flex flex-col gap-1">
      {collapsed ? (
        !first && <div className="mx-2 my-1 border-t border-white/10" />
      ) : (
        <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-white/40">
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom);
  const [mobileOpen, setMobileOpen] = useAtom(sidebarMobileOpenAtom);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { header_links } = useBranding();

  // Auto-collapse sidebar on small screens (below lg = 1024px).
  // Collapses on mount if window is narrow and re-checks on resize.
  useEffect(() => {
    const BREAKPOINT = 1024;

    function handleResize() {
      if (window.innerWidth < BREAKPOINT) {
        setCollapsed(true);
      } else {
        // Close mobile overlay when resizing to desktop
        setMobileOpen(false);
      }
    }

    // Collapse immediately if already below breakpoint
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setCollapsed, setMobileOpen]);

  const { data: unreadData } = useQuery({
    queryKey: ["chat", "unread-count"],
    queryFn: getUnreadCount,
    refetchInterval: 30_000,
  });
  const unreadCount = unreadData?.unread_count ?? 0;

  const { data: newOrdersData } = useQuery({
    queryKey: ["orders", "new-count"],
    queryFn: getNewOrdersCount,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const newOrdersCount = newOrdersData?.count ?? 0;

  // Role gating (preserved exactly from the previous flat sidebar).
  const role = user?.role;
  const canSeeSettings =
    role === "company_admin" || role === "superadmin" || role === "super_admin";
  const canSeeTeam = role === "company_admin" || role === "superadmin";
  const canSeeBilling = role === "company_admin";

  const closeMobile = () => setMobileOpen(false);

  // Small red unread/new-count pill, positioned for collapsed vs expanded modes.
  const renderBadge = (count: number) =>
    count > 0 && (
      <span
        className={cn(
          "pointer-events-none absolute flex items-center justify-center rounded-full bg-red-500 font-bold text-white",
          collapsed
            ? "-right-1 -top-1 h-4 min-w-[16px] px-[3px] text-[10px]"
            : "right-2 top-1/2 h-5 min-w-[20px] -translate-y-1/2 px-1 text-xs",
        )}
      >
        {count > 99 ? "99+" : count}
      </span>
    );

  // OPERATIONS: Analytics, Routes, Orders (badge), Live Tracking, Chat (badge).
  // Sprint 117 (TASK-3828): Dashboard/Reports/Feedback consolidated into Analytics.
  const operationsItems: ReactNode[] = [
    <NavItem
      key="/analytics"
      to="/analytics"
      icon={BarChart3}
      label={t("nav.analytics")}
      collapsed={collapsed}
      onClick={closeMobile}
    />,
    <NavItem
      key="/routes"
      to="/routes"
      icon={Route}
      label={t("nav.routes")}
      collapsed={collapsed}
      onClick={closeMobile}
    />,
    // Orders — rendered with a relative wrapper to support the unread badge.
    <div key="/orders" className="relative">
      <NavItem
        to="/orders"
        icon={ShoppingCart}
        label={t("nav.orders")}
        collapsed={collapsed}
        onClick={closeMobile}
      />
      {renderBadge(newOrdersCount)}
    </div>,
    <NavItem
      key="/tracking"
      to="/tracking"
      icon={MapPin}
      label={t("nav.liveTracking")}
      collapsed={collapsed}
      onClick={closeMobile}
    />,
    // Chat — rendered with a relative wrapper to support the unread badge.
    <div key="/chat" className="relative">
      <NavItem
        to="/chat"
        icon={MessageSquare}
        label={t("nav.chat")}
        collapsed={collapsed}
        onClick={closeMobile}
      />
      {renderBadge(unreadCount)}
    </div>,
  ];

  // RESOURCES: Vehicles, Warehouses, Team (admin-only).
  const resourcesItems: ReactNode[] = [
    <NavItem
      key="/vehicles"
      to="/vehicles"
      icon={Truck}
      label={t("nav.vehicles")}
      collapsed={collapsed}
      onClick={closeMobile}
    />,
    <NavItem
      key="/warehouses"
      to="/warehouses"
      icon={Warehouse}
      label={t("nav.warehouses")}
      collapsed={collapsed}
      onClick={closeMobile}
    />,
  ];
  if (canSeeTeam) {
    resourcesItems.push(
      <NavItem
        key="/users"
        to="/users"
        icon={Users}
        label={t("nav.team")}
        collapsed={collapsed}
        onClick={closeMobile}
      />,
    );
  }

  // ADMINISTRATION: Billing (company_admin only), Settings (admin-only).
  const administrationItems: ReactNode[] = [];
  if (canSeeBilling) {
    administrationItems.push(
      <NavItem
        key="/billing"
        to="/billing"
        icon={Receipt}
        label={t("nav.billing")}
        collapsed={collapsed}
        onClick={closeMobile}
      />,
    );
  }
  if (canSeeSettings) {
    administrationItems.push(
      <NavItem
        key="/settings"
        to="/settings"
        icon={Settings}
        label={t("nav.settings")}
        collapsed={collapsed}
        onClick={closeMobile}
      />,
    );
  }

  // Ordered list of groups; empty groups render nothing (no header, no divider).
  const groups: { titleKey: string; items: ReactNode[] }[] = [
    { titleKey: "nav.groups.operations", items: operationsItems },
    { titleKey: "nav.groups.resources", items: resourcesItems },
    { titleKey: "nav.groups.administration", items: administrationItems },
  ].filter((g) => g.items.length > 0);

  const initials = getUserInitials(user?.first_name, user?.last_name);
  const displayName = user
    ? `${user.first_name} ${user.last_name}`.trim()
    : "User";
  async function handleSignOut() {
    // Await logout so any active impersonation session is ended (best-effort)
    // before navigating away and unmounting the shell.
    await logout();
    void navigate("/login");
  }

  // On mobile the sidebar is an overlay positioned fixed on the left.
  // On desktop (lg+) it is a normal flow element always visible.
  return (
    <aside
      className={cn(
        // Base styles: full height, dark gradient background, transitions
        "flex h-screen flex-col bg-brand-sidebar [background-image:linear-gradient(180deg,hsl(var(--brand-sidebar))_0%,hsl(var(--brand-sidebar)/0.85)_100%)]",
        // Desktop: static in flow, sized by collapsed state
        "lg:relative lg:shrink-0 lg:translate-x-0 lg:transition-[width] lg:duration-200",
        collapsed ? "lg:w-14" : "lg:w-60",
        // Mobile: fixed overlay, slide in/out via transform, always full-width sidebar (w-60)
        "fixed inset-y-0 left-0 z-30 w-60 transition-transform duration-200 lg:static lg:z-auto",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
      )}
    >
      {/* Logo area */}
      <div
        className={cn(
          "shrink-0 border-b border-white/10",
          collapsed
            ? "flex h-14 items-center justify-center lg:flex"
            : "px-4 pb-2 pt-3",
          // On mobile when open, always show full logo (not collapsed)
          "flex h-14 items-center justify-between px-4",
        )}
      >
        {/* On mobile always show logo; on desktop respect collapsed state */}
        <span className="lg:hidden">
          <AppLogo
            variant="dark"
            wordmark
            className="h-7 w-auto max-w-[150px]"
          />
        </span>
        <span className="hidden lg:block">
          {collapsed ? (
            <BrandMark />
          ) : (
            <AppLogo
              variant="dark"
              wordmark
              className="h-7 w-auto max-w-[150px]"
            />
          )}
        </span>

        {/* Close button — mobile only */}
        <button
          type="button"
          aria-label={t("nav.closeMenu")}
          onClick={() => setMobileOpen(false)}
          className="flex items-center justify-center rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
        >
          <X size={18} />
        </button>
      </div>

      {/* Tenant badge */}
      {!collapsed && user?.tenant_name && (
        <div className="shrink-0 px-3 py-2">
          <span className="flex items-center gap-1.5 rounded-md bg-white/8 px-2.5 py-1.5">
            <Building2 size={11} className="shrink-0 text-white/50" />
            <span className="truncate text-xs font-medium text-white/70">
              {user.tenant_name}
            </span>
          </span>
        </div>
      )}

      {/* Nav items — grouped into OPERATIONS / RESOURCES / ADMINISTRATION.
          Empty groups (e.g. ADMINISTRATION for a dispatcher) render nothing. */}
      <nav className="flex flex-1 flex-col overflow-y-auto px-2 py-3">
        {groups.map((group, index) => (
          <NavGroup
            key={group.titleKey}
            title={t(group.titleKey)}
            collapsed={collapsed}
            first={index === 0}
          >
            {group.items}
          </NavGroup>
        ))}

        {/* Brand header links — mobile only (md:hidden). On desktop these live
            in the top Header; the mobile drawer surfaces them here so branded
            links stay reachable when the header nav is hidden. Rendered only
            when the active brand supplies any (default brand → empty → nothing).
            Not gated on `collapsed` — the mobile drawer is always full-width. */}
        {header_links.length > 0 && (
          <div className="mt-4 flex flex-col gap-1 border-t border-white/10 pt-4 md:hidden">
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
              {t("nav.groups.links")}
            </p>
            {header_links.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobile}
                className="flex items-center rounded-lg px-3 py-2 text-sm font-medium text-white/70 outline-none transition-all duration-150 hover:bg-white/8 hover:text-white/90 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/40"
              >
                <span className="truncate">{link.label}</span>
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* Collapse toggle — desktop only */}
      <div className="hidden shrink-0 border-t border-white/10 px-2 py-2 lg:block">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={cn(
            "flex w-full items-center rounded-lg px-3 py-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white",
            collapsed && "justify-center px-2",
          )}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          {!collapsed && (
            <span className="ml-2 text-xs font-medium">
              {t("nav.collapse")}
            </span>
          )}
        </button>
      </div>

      {/* User avatar + dropdown */}
      <div className="shrink-0 border-t border-white/10 px-2 py-3">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className={cn(
                "flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-white/80 transition-colors hover:bg-white/5",
                collapsed && "lg:justify-center",
              )}
            >
              {/* Avatar: image when set, initials circle otherwise */}
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={displayName}
                  className="h-8 w-8 shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold text-white">
                  {initials}
                </span>
              )}
              {/* Always show name/email on mobile; on desktop hide when collapsed */}
              <span
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-1",
                  collapsed && "lg:hidden",
                )}
              >
                <span className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">
                    {displayName}
                  </p>
                  <p className="truncate text-xs text-white/50">
                    {user?.email ?? ""}
                  </p>
                </span>
                <ChevronDown size={14} className="shrink-0 text-white/40" />
              </span>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              side="top"
              align="start"
              sideOffset={4}
              className="z-50 min-w-[200px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
            >
              {/* Non-clickable user info header */}
              <div className="px-3 py-2">
                <p className="text-sm font-semibold text-gray-900">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500">{user?.email ?? ""}</p>
              </div>

              <DropdownMenu.Separator className="my-1 h-px bg-gray-100" />

              <DropdownMenu.Item
                onSelect={() => navigate("/profile")}
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 outline-none hover:bg-gray-50 focus:bg-gray-50"
              >
                <User size={14} />
                {t("nav.profile")}
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 h-px bg-gray-100" />

              <DropdownMenu.Item
                onSelect={() => void handleSignOut()}
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 outline-none hover:bg-gray-50 focus:bg-gray-50"
              >
                <LogOut size={14} />
                {t("nav.signOut")}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </aside>
  );
}
